import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { checkCountForProductMapped } from "../product/ProductSyncRules";

import { onSaveContactSyncRules } from "../contact/ContactSyncRules";
import { onSaveInvoiceSyncRules } from "../invoice/InvoiceSyncRules";
import { onSaveProductSyncRules } from "../product/ProductSyncRules";
import { onSaveMappings } from "../mapping/RenderMappings";

import {
  fetchStatus,
  onChangeProductTab,
  onChangeSaveModalPreference,
  onChangeSelectedTabValues,
} from "../../store/slices/preferenceSlice";
import { checkIsMappingsSaved } from "../../store/slices/mappingSlice";

import { useCmsHook } from "../../helpers/cmsHook";

function InfoModal() {
  const { cmsInstance } = useCmsHook();

  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const {
    saveModalPreference,
    settingsActiveTab,
    invoiceActiveTab,
    productActiveTab,
    contactActiveTab,
    status: { loading, status },
    selectedTabValues,
  } = useSelector((state) => state.preference);

  const {
    hubspotFilterPreference,
    txFilterPreference,
    hubspotCustomerNumberPreference,
    customerNumberPreference,
    isContactSyncRulesSaved,
  } = useSelector((state) => state.contact);

  const { selectedPipelines, isInvoiceSyncRulesSaved, attachPDFToInvoice } =
    useSelector((state) => state.invoice);

  const {
    preferencesForProducts,
    productPreference,
    preferenceForSearchParameters,
    hubspotProductFilterPreference,
    txProductFilterPreference,
    preferenceForCreatingProduct,
    defaultProductForCreating,
    isProductSyncRulesSaved,
  } = useSelector((state) => state.product);

  const {
    mapping: { defaultMappings },
    isMappingSaved,
  } = useSelector((state) => state.mapping);

  const onSaveUnsavedChanges = async () => {
    if (settingsActiveTab === "contact") {
      if (contactActiveTab === "syncRules" && !isContactSyncRulesSaved) {
        await onSaveContactSyncRules({
          setIsLoading,
          searchParams,
          hubspotFilterPreference,
          txFilterPreference,
          hubspotCustomerNumberPreference,
          customerNumberPreference,
          dispatch,
        });
      } else if (
        contactActiveTab === "fieldMappings" &&
        !isMappingSaved.customer
      ) {
        await onSaveMappings({
          dispatch,
          setIsLoading,
          searchParams,
          mapping: defaultMappings.customer,
          mappingsFor: "customer",
        });
      }
    } else if (settingsActiveTab === "order") {
      if (invoiceActiveTab === "syncRules" && !isInvoiceSyncRulesSaved) {
        await onSaveInvoiceSyncRules({
          setIsLoading,
          dispatch,
          searchParams,
          selectedPipelines,
          attachPDFToInvoice,
        });
      } else if (
        invoiceActiveTab === "fieldMappings" &&
        !isMappingSaved.order
      ) {
        await onSaveMappings({
          dispatch,
          setIsLoading,
          searchParams,
          mapping: defaultMappings.order,
          mappingsFor: "order",
        });
      }
    } else if (settingsActiveTab === "product") {
      if (productActiveTab === "syncRules" && !isProductSyncRulesSaved) {
        await onSaveProductSyncRules({
          setIsLoading,
          dispatch,
          searchParams,
          preferencesForProducts,
          productPreference,
          preferenceForSearchParameters,
          hubspotProductFilterPreference,
          txProductFilterPreference,
          preferenceForCreatingProduct,
          defaultProductForCreating,
        });

        if (
          !isMappingSaved.products &&
          preferenceForSearchParameters === "set custom mappings" &&
          checkCountForProductMapped({ mappings: defaultMappings.product }) > 0
        ) {
          await onSaveMappings({
            dispatch,
            setIsLoading,
            searchParams,
            mapping: defaultMappings.product,
            mappingsFor: "product",
            preferencesForProducts,
            productPreference,
            preferenceForSearchParameters,
            hubspotProductFilterPreference,
            txProductFilterPreference,
            preferenceForCreatingProduct,
            defaultProductForCreating,
          });
        }
      } else if (
        productActiveTab === "fieldMappings" &&
        !isMappingSaved.productFieldMappings
      ) {
        await onSaveMappings({
          dispatch,
          setIsLoading,
          searchParams,
          mapping: defaultMappings.productFieldMappings,
          mappingsFor: "productFieldMappings",
        });
      }
    }

    dispatch(onChangeSaveModalPreference({ show: false }));
    dispatch(onChangeSelectedTabValues());
  };

  const onDiscardChanges = () => {
    const portalId = searchParams.get("portalId");

    dispatch(fetchStatus(portalId));

    dispatch(checkIsMappingsSaved({ mappingsFor: "customer" }));
    dispatch(checkIsMappingsSaved({ mappingsFor: "order" }));
    dispatch(checkIsMappingsSaved({ mappingsFor: "productFieldMappings" }));
    dispatch(onChangeSaveModalPreference({ show: false }));

    if (
      selectedTabValues.tab === "product" &&
      selectedTabValues.subTab === "fieldMappings" &&
      !status?.preference?.product?.createProduct?.create
    ) {
      dispatch(onChangeProductTab({ selectedTab: "syncRules" }));
    } else {
      dispatch(onChangeSelectedTabValues());
    }
  };

  const renderContent = () => {
    if (
      selectedPipelines.length === 0 &&
      settingsActiveTab === "order" &&
      invoiceActiveTab === "syncRules"
    ) {
      return (
        <div>
          {cmsInstance &&
            cmsInstance.showRichText(
              "cmsContent.notifications.autoSave.successContent.pipelineAndDealStage.message",
              "auto-save-modal-description"
            )}

          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, document.body.scrollHeight);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else if (
      settingsActiveTab === "product" &&
      productActiveTab === "syncRules" &&
      preferenceForSearchParameters === "set custom mappings" &&
      checkCountForProductMapped({ mappings: defaultMappings.product }) === 0
    ) {
      return (
        <div>
          {cmsInstance &&
            cmsInstance.showRichText(
              "cmsContent.notifications.autoSave.successContent.productMapping.message",
              "auto-save-modal-description"
            )}

          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, 200);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else if (
      settingsActiveTab === "product" &&
      productActiveTab === "syncRules" &&
      ((preferencesForProducts === "select product" && !productPreference) ||
        (preferencesForProducts === "find create product" &&
          preferenceForCreatingProduct &&
          !defaultProductForCreating))
    ) {
      return (
        <div>
          {preferencesForProducts === "select product" &&
            !productPreference &&
            cmsInstance &&
            cmsInstance.showRichText(
              "cmsContent.notifications.autoSave.successContent.defaultProduct.message",
              "auto-save-modal-description"
            )}

          {preferencesForProducts === "select product" &&
            !productPreference && <p>Please select the default product.</p>}

          {preferencesForProducts === "find create product" &&
            preferenceForCreatingProduct &&
            !defaultProductForCreating &&
            cmsInstance &&
            cmsInstance.showRichText(
              "cmsContent.notifications.autoSave.successContent.findCreateProduct.message",
              "auto-save-modal-description"
            )}

          {preferencesForProducts === "find create product" &&
            preferenceForCreatingProduct &&
            !defaultProductForCreating && (
              <p>please select the product in the find create product</p>
            )}

          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, document.body.scrollHeight);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          {cmsInstance &&
            cmsInstance.showRichText(
              "cmsContent.notifications.autoSave.successContent.unSavedChanges.message",
              "auto-save-modal-description"
            )}

          <div className="d-flex flex-row justify-content-end">
            <Button
              className="discard-changes-button"
              onClick={() => onDiscardChanges()}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <span>Discard</span>
              )}
            </Button>
            <Button
              className="ms-3 save-changes-button"
              onClick={() => onSaveUnsavedChanges()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <span>Save changes</span>
              )}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <Modal
      show={saveModalPreference}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
}

export default InfoModal;
